// TODO: Consider modifying this function to use other types of org data
import React, { createContext, useState, useCallback, useRef } from 'react';
import { cloneDeep, isEqual } from 'lodash';
import { formatLastName } from 'utils';
import { UserController } from 'api';
import errorReporter from '@app/errorReporter';
import type { IUserOrgBasicResponse, OrgBasicFilterType } from '@/app-package';
import { BuildJsonType } from '@bb/domain/dist/types';

export type OrgBasicUser = BuildJsonType<IUserOrgBasicResponse[number]>;

export type AgentsContextUser = BuildJsonType<OrgBasicUser & { label: string }>;

const toTitleCase = (str: string) => {
  if (!str) return '';
  return str[0].toUpperCase() + str.slice(1).toLowerCase();
};

// Is a heavy operation to fetch all org users data
// Thus, moved into a context for single fetch per app load
export type AgentsContextType = {
  orgUsers: AgentsContextUser[];
  orgUsersMap: Record<string, AgentsContextUser> | null;
  fetchOrgUsers: (
    filter?: OrgBasicFilterType,
    targetUserIds?: string[],
    orgWide?: boolean,
  ) => Promise<AgentsContextUser[]>;
  fetchHiddenUser?: (hiddenUserId: string) => Promise<AgentsContextUser>;
};
const AgentsContext = createContext<AgentsContextType>({} as any);

export const AgentsContextProvider: React.FC = ({ children }) => {
  const [currentFilter, setCurrentFilter] = useState<
    OrgBasicFilterType | undefined
  >();
  const [currentTargets, setCurrentTargets] = useState<string[]>([]);
  const [orgUsers, setOrgUsers] = useState<AgentsContextUser[]>([]);
  const fetching = useRef(false);

  const formatUsers = ({
    firstName,
    lastName,
    ...user
  }: OrgBasicUser): AgentsContextUser => ({
    ...user,
    firstName: toTitleCase(firstName),
    lastName: formatLastName(lastName)!,
    label: `${toTitleCase(firstName)} ${formatLastName(lastName)}`,
  });

  // Instead of using useEffect to fetch it on initial app load,
  // Fetch the data when it is needed the first time
  const fetchOrgUsers = useCallback(
    async (
      filter?: OrgBasicFilterType,
      targetUserIds: string[] = [],
      orgWide = false,
    ) => {
      const sortedTargets = cloneDeep(targetUserIds).sort();

      if (
        fetching.current ||
        (filter === currentFilter &&
          isEqual(sortedTargets, currentTargets) &&
          orgUsers.length)
      ) {
        return orgUsers;
      }
      // Make sure the place calling this function takes account into
      // duplicate function calls, reducing the performance of the page loading
      try {
        fetching.current = true;
        setCurrentFilter(filter);
        setCurrentTargets(sortedTargets);
        const data = await UserController.orgBasic({
          filter,
          targetUserIds: sortedTargets,
          orgWide,
        });

        const formattedUsers = data.map(formatUsers);
        const sortedUsers = formattedUsers.sort((a, b) =>
          a.firstName > b.firstName ? 1 : -1,
        );
        fetching.current = false;
        setOrgUsers(sortedUsers);
        return sortedUsers;
      } catch (err) {
        errorReporter.error(err);
        console.error('AgentsContext failed to fetch org users:', err);
        return [];
      }
    },
    [orgUsers, currentFilter, currentTargets],
  );

  const fetchHiddenUser = async (hiddenUserId: string) => {
    const data = await UserController.orgBasic({
      targetUserIds: [hiddenUserId],
    });
    const [hiddenUser] = data;

    return formatUsers(hiddenUser);
  };

  const orgUsersMap: Record<string, AgentsContextUser> | null =
    React.useMemo(() => {
      if (!orgUsers?.length) return null;
      const result: typeof orgUsersMap = {};
      orgUsers.forEach(user => {
        result[user.id] = user;
      });
      return result;
    }, [orgUsers]);

  return (
    <AgentsContext.Provider
      value={{ orgUsers, orgUsersMap, fetchOrgUsers, fetchHiddenUser }}
    >
      {children}
    </AgentsContext.Provider>
  );
};

export default AgentsContext;
